import { useCallback, useMemo, useRef, useState } from "react";

export interface PinchPoint {
  x: number;
  y: number;
}

/**
 * Computes the next time domain for a two-finger pinch. The data timestamp
 * under the pinch midpoint stays stationary; the visible range scales by the
 * ratio of the previous to current finger distance, clamped to fullDomain.
 */
export function computePinchDomain(
  previous: [PinchPoint, PinchPoint],
  current: [PinchPoint, PinchPoint],
  domain: [number, number],
  fullDomain: [number, number],
  chartWidth: number,
): [number, number] {
  const prevDist = Math.hypot(
    previous[0].x - previous[1].x,
    previous[0].y - previous[1].y,
  );
  const currDist = Math.hypot(
    current[0].x - current[1].x,
    current[0].y - current[1].y,
  );
  if (prevDist <= 0 || currDist <= 0 || chartWidth <= 0) return domain;
  if (fullDomain[1] - fullDomain[0] <= 0) return domain;

  const [start, end] = domain;
  const range = end - start;
  const newRange = Math.min(
    range * (prevDist / currDist),
    fullDomain[1] - fullDomain[0],
  );

  const midX = (previous[0].x + previous[1].x) / 2;
  const anchorFraction = Math.min(Math.max(midX / chartWidth, 0), 1);
  const anchorTime = start + anchorFraction * range;

  let newStart = anchorTime - anchorFraction * newRange;
  let newEnd = newStart + newRange;
  if (newStart < fullDomain[0]) {
    newStart = fullDomain[0];
    newEnd = newStart + newRange;
  }
  if (newEnd > fullDomain[1]) {
    newEnd = fullDomain[1];
    newStart = newEnd - newRange;
  }
  return [newStart, newEnd];
}

interface UsePinchZoomParams {
  zoomDomain: [number, number] | null;
  fullDomain: [number, number];
  chartWidth: number;
  onZoomChange: (domain: [number, number]) => void;
}

/**
 * Tracks active pointers. Once two are tracked, every move recomputes the
 * zoom domain via computePinchDomain (zooming in from the full domain when
 * zoomDomain is null). Single-pointer gestures are ignored here — panning
 * stays with useChartPanZoom.
 */
export function usePinchZoom({
  zoomDomain,
  fullDomain,
  chartWidth,
  onZoomChange,
}: UsePinchZoomParams) {
  const pointersRef = useRef(new Map<number, PinchPoint>());
  const previousRef = useRef<[PinchPoint, PinchPoint] | null>(null);
  const domainRef = useRef<[number, number] | null>(null);
  const [activePointers, setActivePointers] = useState(0);

  const pinchActive = activePointers >= 2;

  /**
   * Tracks a pointer at the given chart-relative position. Returns true
   * synchronously once at least two pointers are tracked (the Map ref is
   * current, unlike the `pinchActive` render value) so callers can branch on
   * a pinch takeover within the same event.
   */
  const trackPointer = useCallback((pointerId: number, x: number, y: number) => {
    pointersRef.current.set(pointerId, { x, y });
    setActivePointers(pointersRef.current.size);
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      previousRef.current = [a, b];
      domainRef.current = null;
    }
    return pointersRef.current.size >= 2;
  }, []);

  /** Returns true when the move was consumed by an active pinch. */
  const movePointer = useCallback(
    (pointerId: number, x: number, y: number): boolean => {
      if (!pointersRef.current.has(pointerId)) return false;
      pointersRef.current.set(pointerId, { x, y });
      if (pointersRef.current.size !== 2 || !previousRef.current) return false;

      const points = [...pointersRef.current.values()] as [PinchPoint, PinchPoint];
      const base = domainRef.current ?? zoomDomain ?? fullDomain;
      const next = computePinchDomain(
        previousRef.current,
        points,
        base,
        fullDomain,
        chartWidth,
      );
      previousRef.current = points;
      domainRef.current = next;
      onZoomChange(next);
      return true;
    },
    [zoomDomain, fullDomain, chartWidth, onZoomChange],
  );

  /**
   * Stops tracking a pointer. Returns the remaining pointer's last position
   * when exactly one pointer survives the release (a pinch ending 2 → 1), so
   * callers can re-seed a pan gesture from it; otherwise returns null.
   */
  const releasePointer = useCallback((pointerId: number): PinchPoint | null => {
    pointersRef.current.delete(pointerId);
    setActivePointers(pointersRef.current.size);
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      previousRef.current = [a, b];
    } else if (pointersRef.current.size < 2) {
      previousRef.current = null;
      domainRef.current = null;
    }
    if (pointersRef.current.size === 1) {
      return [...pointersRef.current.values()][0]!;
    }
    return null;
  }, []);

  return useMemo(
    () => ({
      pinchActive,
      trackPointer,
      movePointer,
      releasePointer,
    }),
    [pinchActive, trackPointer, movePointer, releasePointer],
  );
}
